import { useMemo } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, ArrowRight, X } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import { getWhatsappConversation } from "@/modules/lead-page/api/lead-page.v2.api";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import WhatsappMessageThread from "@/modules/lead-page/new/components/activity/whatsapp-message-thread";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";

interface WhatsappConversationDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  activityUid: string;
  item: ActivityItem;
  onViewFullChat?: () => void;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());


/* -------------------------------------------------------------------------- */
/*  Main component                                                             */
/* -------------------------------------------------------------------------- */

const DRAWER_WIDTH = "460px";
const CARD_RADIUS = "12px";

const WhatsappConversationDetailDrawer = ({
  open,
  onClose,
  activityUid,
  item,
  onViewFullChat,
}: WhatsappConversationDetailDrawerProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  // Conversation data
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["whatsappConversationDrawer", activityUid],
    ({ pageParam }) =>
      getWhatsappConversation(activityUid, pageParam as string | undefined),
    {
      enabled: open,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
  );

  const allMessages = useMemo(() => {
    if (!data) return [];
    return [...data.pages]
      .reverse()
      .flatMap((p) => p?.messages ?? [])
      .filter(Boolean);
  }, [data]);

  // Sibling fields from the activity row
  const summary = item.whatsappSummary ?? null;
  const sentiment = item.whatsappSentiment ?? null;
  const flags = item.whatsappFlags ?? null;
  const tags = [sentiment?.overall_agent, sentiment?.overall_client].filter(
    (t): t is string => !!t,
  );
  const callOutcome: string | null = flags?.outcome ?? null;
  const requiresEscalation: boolean = !!flags?.requires_escalation;
  const escalationReason: string | null = flags?.escalation_reason ?? null;
  const escalations: string[] =
    requiresEscalation && escalationReason ? [escalationReason] : [];
  const warnings: string[] = Array.isArray(flags?.red_flags)
    ? (flags.red_flags as string[])
    : [];

  const hasSummary = !!summary && summary.trim().length > 0;
  const hasTags = tags.length > 0;
  const hasSummaryCardContent = hasSummary || hasTags;
  const hasCallOutcome = !!callOutcome;
  const hasEscalations = escalations.length > 0;
  const hasWarnings = warnings.length > 0;

  /* ----- Shared content --------------------------------------------------- */

  const renderContent = () => {
    if (isLoading && allMessages.length === 0) {
      return (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xl,
            flex: 1,
          }}
        >
          <CircularProgress size={28} />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Loading conversation...
          </Typography>
        </Box>
      );
    }

    if (isError) {
      return (
        <Box
          role="alert"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.xl,
            textAlign: "center",
          }}
        >
          <AlertTriangle
            size={28}
            color={theme.nawy.colors.semantic.indicator.red}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.semibold,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Couldn&apos;t load this conversation
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
              maxWidth: 280,
            }}
          >
            Something went wrong. Try again in a moment.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              mt: theme.nawy.spacing.xs,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              color: theme.nawy.colors.semantic.indicator.navy,
              textDecoration: "underline",
              "&:hover": { opacity: 0.8 },
            }}
          >
            Try again
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xs,
          // grow into the drawer body so a short conversation still reaches
          // the bottom — the thread card below absorbs the extra space
          flex: "1 0 auto",
        }}
      >
        {/* ── Conversation summary card ── */}
        {hasSummaryCardContent && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              px: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.xs,
              borderRadius: CARD_RADIUS,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            {/* Label + sparkles */}
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                height: 20,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: theme.nawy.colors.semantic.text.tertiary,
                  whiteSpace: "nowrap",
                }}
              >
                Conversation summary
              </Typography>
              <SparklesGradientIcon size={16} />
            </Box>

            {/* Tags */}
            {hasTags && (
              <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {tags.map((tag, i) => (
                  <Chip
                    key={i}
                    label={tag}
                    size="small"
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: "18px",
                      color: theme.nawy.colors.semantic.text.primary,
                      backgroundColor: theme.nawy.colors.semantic.layer.primary,
                      borderRadius: "4px",
                      height: "auto",
                      "& .MuiChip-label": {
                        px: theme.nawy.spacing.xs,
                        py: "2px",
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Summary text */}
            {hasSummary && (
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                {summary}
              </Typography>
            )}
          </Box>
        )}

        {/* ── Conversation outcome card ── */}
        {hasCallOutcome && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              p: "12px",
              borderRadius: CARD_RADIUS,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              Conversation outcome
            </Typography>
            <Chip
              label={formatLabel(callOutcome!)}
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                backgroundColor: theme.nawy.colors.semantic.indicator.blue,
                color: theme.nawy.colors.semantic.indicator.onColor,
                borderRadius: "4px",
                height: "auto",
                "& .MuiChip-label": { px: theme.nawy.spacing.xs, py: "2px" },
              }}
            />
          </Box>
        )}

        {/* ── Escalation banner ── */}
        {hasEscalations && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              p: "12px",
              borderRadius: CARD_RADIUS,
              backgroundColor: "#feeceb",
            }}
          >
            {escalations.map((msg, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: theme.nawy.colors.semantic.indicator.red,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    !
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {msg}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Warnings card ── */}
        {hasWarnings && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              p: "12px",
              borderRadius: CARD_RADIUS,
              backgroundColor:
                theme.nawy.colors.semantic.layer.extension.warningLight,
            }}
          >
            {warnings.map((msg, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <AlertCircle
                  size={16}
                  color={theme.nawy.colors.semantic.indicator.orange}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {msg}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Whatsapp thread label + separator (above the card) ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            mt: theme.nawy.spacing.xs,
          }}
        >
          <Box
            sx={{
              height: "1px",
              width: "100%",
              backgroundColor: theme.nawy.colors.semantic.border.secondary,
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "16px",
              color: theme.nawy.colors.semantic.text.tertiary,
              whiteSpace: "nowrap",
            }}
          >
            Whatsapp thread
          </Typography>
        </Box>

        {/* ── Conversation thread ── */}
        <WhatsappMessageThread
          messages={allMessages}
          hasOlder={!!hasNextPage}
          isLoadingOlder={isFetchingNextPage}
          onLoadOlder={fetchNextPage}
          fillHeight
        />

        {/* View full chat link (outside the thread) */}
        {onViewFullChat && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mt: theme.nawy.spacing.xs,
            }}
          >
            <Box
              component="button"
              onClick={onViewFullChat}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                color: theme.nawy.colors.semantic.text.accent,
                "&:hover": { opacity: 0.8 },
              }}
            >
              View full chat
              <ArrowRight size={16} aria-hidden />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  /* ----- Mobile bottom sheet --------------------------------------------- */

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title="WhatsApp details"
        snapPoints={[1]}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            px: theme.nawy.spacing.md,
            py: theme.nawy.spacing.sm,
            overflowY: "auto",
          }}
        >
          {renderContent()}
        </Box>
      </MobileModal>
    );
  }

  /* ----- Desktop right-anchored drawer ----------------------------------- */

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1400,
        },
      }}
    >
      {/* Header — 52px, "Whatsapp details" + X, border-bottom */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.md,
          height: 52,
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.xs,
          borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            ...theme.nawy.typography.english.variants.headline.xs,
            color: theme.nawy.colors.semantic.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Whatsapp details
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            color: theme.nawy.colors.semantic.text.primary,
            flexShrink: 0,
            px: "10px",
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.secondary,
            },
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {/* Content area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          p: theme.nawy.spacing.md,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: theme.nawy.colors.semantic.border.secondary,
            borderRadius: 3,
          },
        }}
      >
        {renderContent()}
      </Box>
    </Drawer>
  );
};

export default WhatsappConversationDetailDrawer;
